import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Minus, Eraser } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { sportStatsService } from '../../services/sportStatsService';
import type { Sport } from '../../types/core';

interface StatWithId {
  id: string;
  stat_key: string;
  stat_value: number;
}

interface AddGameStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerCardId: string; // Can be empty if selecting player
  teamId: string;
  sport: Sport;
  onStatsAdded: () => void;
  gameId?: string; // Optional game ID to link stats to a specific game
  existingStats?: StatWithId[]; // Optional existing stats for editing
  /** Player display name shown at top so coach knows whose stats they're editing */
  playerName?: string;
}

type PlateAppearanceResult = 
  | '1B' | '2B' | '3B' | 'HR'  // Hits
  | 'BB' | 'HBP' | 'SO'         // Other outcomes
  | 'SF' | 'FC' | 'E' | 'FO'   // Other
  | '';

interface PlateAppearance {
  id: string;
  result: PlateAppearanceResult;
}

const BATTING_STAT_KEYS = ['1B', '2B', '3B', 'HR', 'BB', 'HBP', 'SO', 'SF', 'FC', 'E', 'FO', 'AB', 'H', 'R', 'SB', 'RBI'];
const PITCHING_STAT_KEYS = ['IP', 'K', 'BB_Allowed', 'H_Allowed', 'R_Allowed', 'ER', 'HBP_Allowed', 'WP', 'Balk', 'Pitches', 'Balls', 'Strikes', 'PickOffs', 'Outs'];

const PLATE_APPEARANCE_OPTIONS: Array<{ value: PlateAppearanceResult; label: string; description: string }> = [
  { value: '1B', label: 'Single (1B)', description: 'Single base hit' },
  { value: '2B', label: 'Double (2B)', description: 'Double base hit' },
  { value: '3B', label: 'Triple (3B)', description: 'Triple base hit' },
  { value: 'HR', label: 'Home Run (HR)', description: 'Home run' },
  { value: 'BB', label: 'Walk (BB)', description: 'Base on balls' },
  { value: 'HBP', label: 'Hit By Pitch (HBP)', description: 'Hit by pitch' },
  { value: 'SO', label: 'Strikeout (SO)', description: 'Strikeout' },
  { value: 'SF', label: 'Sacrifice Fly (SF)', description: 'Sacrifice fly' },
  { value: 'FC', label: 'Fielder\'s Choice (FC)', description: 'Fielder\'s choice' },
  { value: 'E', label: 'Error (E)', description: 'Reached on error' },
  { value: 'FO', label: 'Fly Out (FO)', description: 'Fly out' },
];

export const AddGameStatsModal: React.FC<AddGameStatsModalProps> = ({
  isOpen,
  onClose,
  playerCardId,
  teamId,
  sport,
  onStatsAdded,
  gameId,
  existingStats,
  playerName,
}) => {
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [opponent, setOpponent] = useState<string>('');
  const [season, setSeason] = useState<string>('');

  const [existingStatIds, setExistingStatIds] = useState<Map<string, string[]>>(new Map()); // stat_key -> stat_ids[] for deletion
  const [deletedStatIds, setDeletedStatIds] = useState<Set<string>>(new Set()); // ids already deleted by Clear Batting/Pitching

  // Load game details and existing stats if editing
  useEffect(() => {
    if (gameId && isOpen) {
      loadGameDetails();
    }
    if (existingStats && isOpen) {
      loadExistingStats();
    } else if (!existingStats && isOpen) {
      resetForm();
    }
  }, [gameId, isOpen, existingStats]);

  const loadGameDetails = async () => {
    if (!gameId) return;
    try {
      const { gameService } = await import('../../services/gameService');
      const game = await gameService.getGame(gameId);
      if (game) {
        setDate(game.date);
        setOpponent(game.opponent_name);
        if (game.season) setSeason(game.season);
      }
    } catch (error) {
      console.error('Error loading game details:', error);
    }
  };

  const loadExistingStats = async () => {
    if (!existingStats) return;
    setDeletedStatIds(new Set());
    const statIdsMap = new Map<string, string[]>();
    let runsScoredVal = '';
    let stolenBasesVal = '';
    let rbisVal = '';
    let strikeoutsVal = '';
    let ballsVal = '';
    let strikesVal = '';
    let pitchesThrownVal = '';
    let pickOffsVal = '';
    let outsGeneratedVal = '';
    let ipVal = '';
    let bbAllowedVal = '';
    let bfVal = '';
    let hAllowedVal = '';
    let runsAllowedVal = '';
    let earnedRunsVal = '';
    let hbpAllowedVal = '';
    let wpVal = '';
    let balkVal = '';

    const paCounts: Record<string, number> = {};

    existingStats.forEach(stat => {
      if (!statIdsMap.has(stat.stat_key)) {
        statIdsMap.set(stat.stat_key, []);
      }
      statIdsMap.get(stat.stat_key)!.push(stat.id);

      if (['1B', '2B', '3B', 'HR', 'BB', 'HBP', 'SO', 'SF', 'FC', 'E', 'FO'].includes(stat.stat_key)) {
        paCounts[stat.stat_key] = (paCounts[stat.stat_key] || 0) + stat.stat_value;
      }

      if (stat.stat_key === 'R') {
        runsScoredVal = (parseFloat(runsScoredVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'SB') {
        stolenBasesVal = (parseFloat(stolenBasesVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'RBI') {
        rbisVal = (parseFloat(rbisVal || '0') + stat.stat_value).toString();
      }

      if (stat.stat_key === 'K') {
        strikeoutsVal = (parseFloat(strikeoutsVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'Balls') {
        ballsVal = (parseFloat(ballsVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'Strikes') {
        strikesVal = (parseFloat(strikesVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'Pitches') {
        pitchesThrownVal = (parseFloat(pitchesThrownVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'PickOffs') {
        pickOffsVal = (parseFloat(pickOffsVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'Outs') {
        outsGeneratedVal = (parseFloat(outsGeneratedVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'IP') {
        ipVal = (parseFloat(ipVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'BB_Allowed') {
        bbAllowedVal = (parseFloat(bbAllowedVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'BF') {
        bfVal = (parseFloat(bfVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'H_Allowed') {
        hAllowedVal = (parseFloat(hAllowedVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'R_Allowed') {
        runsAllowedVal = (parseFloat(runsAllowedVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'ER') {
        earnedRunsVal = (parseFloat(earnedRunsVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'HBP_Allowed') {
        hbpAllowedVal = (parseFloat(hbpAllowedVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'WP') {
        wpVal = (parseFloat(wpVal || '0') + stat.stat_value).toString();
      } else if (stat.stat_key === 'Balk') {
        balkVal = (parseFloat(balkVal || '0') + stat.stat_value).toString();
      }
    });

    // Prefer stored PA order when editing a game so order is preserved
    let paStats: PlateAppearance[];
    if (gameId) {
      try {
        const sequence = await sportStatsService.getGameBattingSequence(gameId, playerCardId);
        if (sequence && sequence.length > 0) {
          paStats = sequence.map((result, i) => ({
            id: `pa_${i}_${Date.now()}`,
            result: result as PlateAppearanceResult,
          }));
        } else {
          paStats = reconstructPaFromCounts(paCounts);
        }
      } catch {
        paStats = reconstructPaFromCounts(paCounts);
      }
    } else {
      paStats = reconstructPaFromCounts(paCounts);
    }

    setExistingStatIds(statIdsMap);
    setPlateAppearances(paStats.length > 0 ? paStats : [{ id: '1', result: '' }]);
    setRunsScored(runsScoredVal);
    setStolenBases(stolenBasesVal);
    setRbis(rbisVal);
    setStrikeouts(strikeoutsVal);
    setBalls(ballsVal);
    setStrikes(strikesVal);
    setPitchesThrown(pitchesThrownVal);
    setPickOffs(pickOffsVal);
    setOutsGenerated(outsGeneratedVal);
    setInningsPitched(ipVal);
    setWalksAllowed(bbAllowedVal);
    setBattersFaced(bfVal);
    setHitsAllowed(hAllowedVal);
    setRunsAllowed(runsAllowedVal);
    setEarnedRuns(earnedRunsVal);
    setHBPAllowed(hbpAllowedVal);
    setWildPitches(wpVal);
    setBalks(balkVal);
  };

  function reconstructPaFromCounts(paCounts: Record<string, number>): PlateAppearance[] {
    const paStats: PlateAppearance[] = [];
    Object.entries(paCounts).forEach(([key, count]) => {
      for (let i = 0; i < count; i++) {
        paStats.push({
          id: `${key}_${Date.now()}_${i}`,
          result: key as PlateAppearanceResult,
        });
      }
    });
    return paStats;
  }

  const resetForm = () => {
    setDate(new Date().toISOString().split('T')[0]);
    setOpponent('');
    setSeason('');
    setPlateAppearances([{ id: '1', result: '' }]);
    setRunsScored('');
    setStolenBases('');
    setRbis('');
    setStrikeouts('');
    setBalls('');
    setStrikes('');
    setPitchesThrown('');
    setPickOffs('');
    setOutsGenerated('');
    setInningsPitched('');
    setWalksAllowed('');
    setBattersFaced('');
    setHitsAllowed('');
    setRunsAllowed('');
    setEarnedRuns('');
    setHBPAllowed('');
    setWildPitches('');
    setBalks('');
    setExistingStatIds(new Map());
    setDeletedStatIds(new Set());
  };
  const [plateAppearances, setPlateAppearances] = useState<PlateAppearance[]>([
    { id: '1', result: '' },
  ]);
  // Generic batting stats
  const [runsScored, setRunsScored] = useState<string>('');
  const [stolenBases, setStolenBases] = useState<string>('');
  const [rbis, setRbis] = useState<string>('');
  // Pitching/Defensive stats
  const [strikeouts, setStrikeouts] = useState<string>(''); // K - pitching strikeouts
  const [balls, setBalls] = useState<string>('');
  const [strikes, setStrikes] = useState<string>('');
  const [pitchesThrown, setPitchesThrown] = useState<string>('');
  const [pickOffs, setPickOffs] = useState<string>('');
  const [outsGenerated, setOutsGenerated] = useState<string>('');
  const [inningsPitched, setInningsPitched] = useState<string>('');
  const [walksAllowed, setWalksAllowed] = useState<string>('');
  const [battersFaced, setBattersFaced] = useState<string>('');
  const [hitsAllowed, setHitsAllowed] = useState<string>('');
  const [runsAllowed, setRunsAllowed] = useState<string>('');
  const [earnedRuns, setEarnedRuns] = useState<string>('');
  const [hbpAllowed, setHBPAllowed] = useState<string>('');
  const [wildPitches, setWildPitches] = useState<string>('');
  const [balks, setBalks] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Only show for baseball/softball
  if (sport !== 'Baseball' && sport !== 'Softball') {
    return null;
  }

  const handleAddPlateAppearance = () => {
    setPlateAppearances([
      ...plateAppearances,
      { id: Date.now().toString(), result: '' },
    ]);
  };

  const handleRemovePlateAppearance = (id: string) => {
    if (plateAppearances.length > 1) {
      setPlateAppearances(plateAppearances.filter(pa => pa.id !== id));
    }
  };

  const handlePlateAppearanceChange = (id: string, result: PlateAppearanceResult) => {
    setPlateAppearances(plateAppearances.map(pa => 
      pa.id === id ? { ...pa, result } : pa
    ));
  };

  const calculateStatsFromPlateAppearances = () => {
    const stats: Record<string, number> = {
      'AB': 0,  // At bats (excludes BB, HBP, SF, but includes SO, FO, FC, E)
      'H': 0,   // Hits
      '1B': 0,
      '2B': 0,
      '3B': 0,
      'HR': 0,
      'BB': 0,
      'HBP': 0,
      'SO': 0,
      'SF': 0,
      'FC': 0,  // Fielder's choice
      'E': 0,   // Reached on error
      'FO': 0,  // Fly out
    };

    plateAppearances.forEach(pa => {
      if (!pa.result) return;

      // Count at bats (excludes BB, HBP, SF)
      // AB includes: hits, strikeouts, fly outs, fielder's choice, errors
      if (pa.result !== 'BB' && pa.result !== 'HBP' && pa.result !== 'SF') {
        stats['AB']++;
      }

      // Count hits
      if (pa.result === '1B' || pa.result === '2B' || pa.result === '3B' || pa.result === 'HR') {
        stats['H']++;
        stats[pa.result]++;
      }

      // Count other outcomes (all PA result types so they are saved to DB)
      if (pa.result === 'BB') stats['BB']++;
      if (pa.result === 'HBP') stats['HBP']++;
      if (pa.result === 'SO') stats['SO']++;
      if (pa.result === 'SF') stats['SF']++;
      if (pa.result === 'FC') stats['FC']++;
      if (pa.result === 'E') stats['E']++;
      if (pa.result === 'FO') stats['FO']++;
    });

    return stats;
  };

  const handleClearBattingData = async () => {
    if (!existingStats?.length || !gameId) return;
    const idsToDelete: string[] = [];
    BATTING_STAT_KEYS.forEach(key => {
      (existingStatIds.get(key) || []).forEach(id => idsToDelete.push(id));
    });
    if (idsToDelete.length === 0) {
      toast('No batting data to clear');
      return;
    }
    const confirmed = window.confirm(
      'Are you sure you want to erase all batting data for this game for this player? This cannot be undone.'
    );
    if (!confirmed) return;
    setSaving(true);
    try {
      await Promise.all(idsToDelete.map(id => sportStatsService.deleteStat(id)));
      setDeletedStatIds(prev => new Set([...prev, ...idsToDelete]));
      await sportStatsService.deleteGameBattingSequence(gameId, playerCardId);
      setPlateAppearances([{ id: '1', result: '' }]);
      setRunsScored('');
      setStolenBases('');
      setRbis('');
      setExistingStatIds(prev => {
        const next = new Map(prev);
        BATTING_STAT_KEYS.forEach(k => next.delete(k));
        return next;
      });
      toast.success('Batting data cleared');
      onStatsAdded();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to clear batting data');
    } finally {
      setSaving(false);
    }
  };

  const handleClearPitchingData = async () => {
    if (!existingStats?.length) return;
    const idsToDelete: string[] = [];
    PITCHING_STAT_KEYS.forEach(key => {
      (existingStatIds.get(key) || []).forEach(id => idsToDelete.push(id));
    });
    if (idsToDelete.length === 0) {
      toast('No pitching data to clear');
      return;
    }
    const confirmed = window.confirm(
      'Are you sure you want to erase all pitching data for this game for this player? This cannot be undone.'
    );
    if (!confirmed) return;
    setSaving(true);
    try {
      await Promise.all(idsToDelete.map(id => sportStatsService.deleteStat(id)));
      setDeletedStatIds(prev => new Set([...prev, ...idsToDelete]));
      setStrikeouts('');
      setBalls('');
      setStrikes('');
      setPitchesThrown('');
      setPickOffs('');
      setOutsGenerated('');
      setInningsPitched('');
      setWalksAllowed('');
      setBattersFaced('');
      setHitsAllowed('');
      setRunsAllowed('');
      setEarnedRuns('');
      setHBPAllowed('');
      setWildPitches('');
      setBalks('');
      setExistingStatIds(prev => {
        const next = new Map(prev);
        PITCHING_STAT_KEYS.forEach(k => next.delete(k));
        return next;
      });
      toast.success('Pitching data cleared');
      onStatsAdded();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to clear pitching data');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!date) {
      toast.error('Please select a date');
      return;
    }

    // Check that all plate appearances have results
    const incomplete = plateAppearances.some(pa => !pa.result);
    if (incomplete) {
      toast.error('Please select a result for all plate appearances');
      return;
    }

    setSaving(true);
    try {
      // Calculate stats from plate appearances
      const calculatedStats = calculateStatsFromPlateAppearances();

      // Collect all stats to save
      const statsToSave: Array<{ key: string; value: number }> = [];

      // Add plate appearance stats
      Object.entries(calculatedStats)
        .filter(([_, value]) => value > 0)
        .forEach(([key, value]) => {
          statsToSave.push({ key, value });
        });

      // Add generic batting stats
      if (runsScored && !isNaN(parseFloat(runsScored))) {
        statsToSave.push({ key: 'R', value: parseFloat(runsScored) });
      }
      if (stolenBases && !isNaN(parseFloat(stolenBases))) {
        statsToSave.push({ key: 'SB', value: parseFloat(stolenBases) });
      }
      if (rbis && !isNaN(parseFloat(rbis))) {
        statsToSave.push({ key: 'RBI', value: parseFloat(rbis) });
      }

      // Add pitching/defensive stats
      if (strikeouts && !isNaN(parseFloat(strikeouts))) {
        statsToSave.push({ key: 'K', value: parseFloat(strikeouts) }); // Pitching strikeouts
      }
      if (balls && !isNaN(parseFloat(balls))) {
        statsToSave.push({ key: 'Balls', value: parseFloat(balls) });
      }
      if (strikes && !isNaN(parseFloat(strikes))) {
        statsToSave.push({ key: 'Strikes', value: parseFloat(strikes) });
      }
      if (pitchesThrown && !isNaN(parseFloat(pitchesThrown))) {
        statsToSave.push({ key: 'Pitches', value: parseFloat(pitchesThrown) });
      }
      if (pickOffs && !isNaN(parseFloat(pickOffs))) {
        statsToSave.push({ key: 'PickOffs', value: parseFloat(pickOffs) });
      }
      if (outsGenerated && !isNaN(parseFloat(outsGenerated))) {
        statsToSave.push({ key: 'Outs', value: parseFloat(outsGenerated) });
      }
      if (inningsPitched && !isNaN(parseFloat(inningsPitched))) {
        statsToSave.push({ key: 'IP', value: parseFloat(inningsPitched) });
      }
      if (walksAllowed && !isNaN(parseFloat(walksAllowed))) {
        statsToSave.push({ key: 'BB_Allowed', value: parseFloat(walksAllowed) });
      }
      if (battersFaced && !isNaN(parseFloat(battersFaced))) {
        statsToSave.push({ key: 'BF', value: parseFloat(battersFaced) });
      }
      if (hitsAllowed && !isNaN(parseFloat(hitsAllowed))) {
        statsToSave.push({ key: 'H_Allowed', value: parseFloat(hitsAllowed) });
      }
      if (runsAllowed && !isNaN(parseFloat(runsAllowed))) {
        statsToSave.push({ key: 'R_Allowed', value: parseFloat(runsAllowed) });
      }
      if (earnedRuns && !isNaN(parseFloat(earnedRuns))) {
        statsToSave.push({ key: 'ER', value: parseFloat(earnedRuns) });
      }
      if (hbpAllowed && !isNaN(parseFloat(hbpAllowed))) {
        statsToSave.push({ key: 'HBP_Allowed', value: parseFloat(hbpAllowed) });
      }
      if (wildPitches && !isNaN(parseFloat(wildPitches))) {
        statsToSave.push({ key: 'WP', value: parseFloat(wildPitches) });
      }
      if (balks && !isNaN(parseFloat(balks))) {
        statsToSave.push({ key: 'Balk', value: parseFloat(balks) });
      }

      // If editing, delete all old stats (except those already cleared) and add new ones
      if (existingStats && existingStats.length > 0) {
        const toDelete = existingStats.filter(stat => !deletedStatIds.has(stat.id));
        if (toDelete.length > 0) {
          await Promise.all(toDelete.map(stat => sportStatsService.deleteStat(stat.id)));
        }
      }

      // Add all stats (either new or re-added after deletion)
      const statPromises = statsToSave.map(({ key, value }) =>
        sportStatsService.addStat(
          playerCardId,
          teamId,
          key,
          value,
          date,
          gameId || undefined,
          opponent || undefined,
          season || undefined
        )
      );
      await Promise.all(statPromises);

      // Save plate appearance order so it is preserved when editing
      if (gameId) {
        await sportStatsService.saveGameBattingSequence(
          gameId,
          playerCardId,
          teamId,
          date,
          plateAppearances.map(pa => pa.result)
        );
      }

      toast.success(existingStats ? 'Game stats updated successfully' : 'Game stats added successfully');
      
      // Reset form
      resetForm();
      
      onStatsAdded();
      onClose();
    } catch (error: any) {
      console.error('Error adding game stats:', error);
      toast.error(error?.message || 'Failed to add game stats');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const tileClass = 'rounded-lg border border-gray-200 bg-white p-2 shadow-sm hover:border-gray-300 focus-within:ring-2 focus-within:ring-primary focus-within:border-primary';
  const stepVal = (v: string, delta: number, step: number) => {
    const n = parseFloat(v) || 0;
    const next = n + delta;
    if (next <= 0) return '0';
    return step === 0.1 ? String(Math.round(next * 10) / 10) : String(Math.floor(next));
  };
  const StepperField = ({
    value,
    set,
    step = 1,
    placeholder = '0',
  }: {
    value: string;
    set: (s: string) => void;
    step?: number;
    placeholder?: string;
  }) => {
    const handleMinus = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      set(stepVal(value, -step, step));
    };
    const handlePlus = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      set(stepVal(value, step, step));
    };
    return (
      <div className="flex items-center rounded-md border border-gray-300 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary w-[96px]">
        <button
          type="button"
          onClick={handleMinus}
          className="flex-shrink-0 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 py-1.5 cursor-pointer select-none min-w-0"
          aria-label="Decrease"
        >
          <Minus size={10} strokeWidth={2.5} />
        </button>
        <input
          type="number"
          min={0}
          step={step}
          value={value}
          onChange={(e) => set(e.target.value)}
          className="flex-shrink-0 w-8 min-w-8 text-center text-sm py-1.5 border-0 bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={handlePlus}
          className="flex-shrink-0 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 py-1.5 cursor-pointer select-none min-w-0"
          aria-label="Increase"
        >
          <Plus size={10} strokeWidth={2.5} />
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-3xl w-full flex flex-col sm:max-h-[90vh]" style={{ 
        maxHeight: 'calc(100vh - 60px)',
        height: 'calc(100vh - 60px)'
      }}>
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{existingStats ? 'Edit Game Statistics' : 'Add Game Statistics'}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          {playerName && (
            <p className="text-sm font-medium text-primary truncate" title={playerName}>
              {playerName}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
          <form id="game-stats-form" onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4">
          {/* Game info - one row */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date <span className="text-red-500">*</span></label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Opponent</label>
              <input type="text" value={opponent} onChange={(e) => setOpponent(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Optional" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Season</label>
              <input type="text" value={season} onChange={(e) => setSeason(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="e.g. 2024" />
            </div>
          </div>

          {/* ——— BATTING ——— */}
          <div className="rounded-xl border-2 border-amber-200 bg-amber-50/40 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-amber-900 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500" /> Batting
              </h3>
              {existingStats && existingStats.length > 0 && gameId && (
                <button
                  type="button"
                  onClick={handleClearBattingData}
                  disabled={saving}
                  className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-100 px-2 py-1 rounded border border-amber-200 disabled:opacity-50"
                  title="Clear all batting data for this game"
                >
                  <Eraser size={12} /> Clear batting
                </button>
              )}
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-amber-800">Plate appearances <span className="text-red-500">*</span></span>
              <button type="button" onClick={handleAddPlateAppearance} className="btn-secondary flex items-center gap-1 text-xs px-2 py-1 rounded">
                <Plus size={12} /> Add PA
              </button>
            </div>
            <div className="space-y-2 mb-3">
              {plateAppearances.map((pa, index) => (
                <div key={pa.id} className="flex items-center gap-2 py-1.5 px-2 bg-white rounded-lg border border-amber-100">
                  <span className="flex-shrink-0 w-6 text-xs font-medium text-gray-500">#{index + 1}</span>
                  <select
                    value={pa.result}
                    onChange={(e) => handlePlateAppearanceChange(pa.id, e.target.value as PlateAppearanceResult)}
                    className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="">Result...</option>
                    {PLATE_APPEARANCE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {plateAppearances.length > 1 && (
                    <button type="button" onClick={() => handleRemovePlateAppearance(pa.id)} className="flex-shrink-0 p-1.5 text-red-600 hover:bg-red-50 rounded">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                { label: 'R', value: runsScored, set: setRunsScored },
                { label: 'SB', value: stolenBases, set: setStolenBases },
                { label: 'RBI', value: rbis, set: setRbis },
              ].map(({ label, value, set }) => (
                <div key={label} className={tileClass}>
                  <div className="text-xs font-semibold text-amber-800 mb-1">{label}</div>
                  <StepperField value={value} set={set} />
                </div>
              ))}
            </div>
            {plateAppearances.some(pa => pa.result) && (
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-amber-800">
                {Object.entries(calculateStatsFromPlateAppearances()).filter(([, v]) => v > 0).map(([k, v]) => (
                  <span key={k}><strong>{k}</strong> {v}</span>
                ))}
              </div>
            )}
          </div>

          {/* ——— PITCHING ——— */}
          <div className="rounded-xl border-2 border-blue-200 bg-blue-50/40 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-blue-900 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500" /> Pitching
              </h3>
              {existingStats && existingStats.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearPitchingData}
                  disabled={saving}
                  className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 hover:bg-blue-100 px-2 py-1 rounded border border-blue-200 disabled:opacity-50"
                  title="Clear all pitching data for this game"
                >
                  <Eraser size={12} /> Clear pitching
                </button>
              )}
            </div>
            <p className="text-xs text-blue-800/80 mb-3">Only if this player pitched in this game.</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {[
                { label: 'IP', value: inningsPitched, set: setInningsPitched },
                { label: 'BF', value: battersFaced, set: setBattersFaced },
                { label: 'K', value: strikeouts, set: setStrikeouts },
                { label: 'BB', value: walksAllowed, set: setWalksAllowed },
                { label: 'H allowed', value: hitsAllowed, set: setHitsAllowed },
                { label: 'R allowed', value: runsAllowed, set: setRunsAllowed },
                { label: 'ER', value: earnedRuns, set: setEarnedRuns },
                { label: 'HBP', value: hbpAllowed, set: setHBPAllowed },
                { label: 'WP', value: wildPitches, set: setWildPitches },
                { label: 'Balk', value: balks, set: setBalks },
                { label: 'Pitches', value: pitchesThrown, set: setPitchesThrown },
                { label: 'Balls', value: balls, set: setBalls },
                { label: 'Strikes', value: strikes, set: setStrikes },
                { label: 'Pick off', value: pickOffs, set: setPickOffs },
                { label: 'Outs', value: outsGenerated, set: setOutsGenerated },
              ].map(({ label, value, set, step }) => (
                <div key={label} className={tileClass}>
                  <div className="text-xs font-semibold text-blue-800 mb-1">{label}</div>
                  <StepperField value={value} set={set} step={step ?? 1} />
                </div>
              ))}
            </div>
          </div>

          </form>
        </div>

        {/* Actions - Outside scrollable area but inside form container */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 sm:px-6 py-4">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="game-stats-form"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {saving ? 'Saving...' : existingStats ? 'Save Changes' : 'Save Game Stats'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

